import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameDay, parseISO, addMonths, subMonths, endOfDay, startOfWeek, endOfWeek, addDays, subDays,
} from 'date-fns';
import { pl } from 'date-fns/locale';
import api from '../utils/api';
import BottomNav from '../components/BottomNav';
import AppointmentCard from '../components/AppointmentCard';
import AddAppointmentModal from '../components/AddAppointmentModal';
import AddBlockModal from '../components/AddBlockModal';
import PanelToast from '../components/PanelToast';
import RescheduleAppointmentModal from '../components/RescheduleAppointmentModal';
import KpiStrip from '../components/KpiStrip';
import ConfirmDialog from '../components/ConfirmDialog';
import { openPhoneDialer, openSmsComposer } from '../utils/deviceActions';
import { isAppointmentResolved } from '../utils/appointmentStatus';

const TIMELINE_HOUR_HEIGHT = 72;

function getDayBounds(day) {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);

  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);

  return { dayStart, dayEnd };
}

function clipIntervalToDay(start, end, day) {
  const { dayStart, dayEnd } = getDayBounds(day);
  const clippedStart = start > dayStart ? start : dayStart;
  const clippedEnd = end < dayEnd ? end : dayEnd;

  if (clippedStart >= clippedEnd) {
    return null;
  }

  return { start: clippedStart, end: clippedEnd };
}

function getMinutesOfDay(date) {
  return date.getHours() * 60 + date.getMinutes();
}

function getTimelineBounds(weekDays) {
  let minMinutes = 8 * 60;
  let maxMinutes = 18 * 60;

  weekDays.forEach((day) => {
    day.appointments.forEach((appointment) => {
      const start = new Date(appointment.scheduled_at);
      const durationMinutes = Math.max(Number(appointment.duration_minutes || 0), 30);
      const end = new Date(start.getTime() + durationMinutes * 60000);
      minMinutes = Math.min(minMinutes, getMinutesOfDay(start));
      maxMinutes = Math.max(maxMinutes, getMinutesOfDay(end));
    });

    day.blocks.forEach((block) => {
      minMinutes = Math.min(minMinutes, getMinutesOfDay(block.start));
      maxMinutes = Math.max(maxMinutes, getMinutesOfDay(block.end));
    });
  });

  const startHour = Math.max(6, Math.floor((minMinutes - 30) / 60));
  const endHour = Math.min(23, Math.ceil((maxMinutes + 30) / 60));
  const normalizedEndHour = Math.min(23, endHour <= startHour ? startHour + 10 : endHour);

  return {
    startHour,
    endHour: normalizedEndHour,
    totalMinutes: (normalizedEndHour - startHour) * 60,
  };
}

function isFullDayVacation(block) {
  const isVacation = block.block_type === 'vacation' || block.reason === 'Urlop';
  if (!isVacation) {
    return false;
  }

  const start = parseISO(block.blocked_from);
  const end = parseISO(block.blocked_until);

  return format(start, 'HH:mm') === '00:00' && format(end, 'HH:mm') === '23:59';
}

function formatBlockRange(block) {
  const start = parseISO(block.blocked_from);
  const end = parseISO(block.blocked_until);

  if (isFullDayVacation(block)) {
    return `${format(start, 'd MMM yyyy', { locale: pl })} - ${format(end, 'd MMM yyyy', { locale: pl })}`;
  }

  if (isSameDay(start, end)) {
    return `${format(start, 'd MMM HH:mm', { locale: pl })} - ${format(end, 'HH:mm')}`;
  }

  return `${format(start, 'd MMM HH:mm', { locale: pl })} - ${format(end, 'd MMM HH:mm', { locale: pl })}`;
}

function normalizeBlockInterval(block) {
  const start = parseISO(block.blocked_from);
  const end = parseISO(block.blocked_until);

  if (!isFullDayVacation(block)) {
    return { start, end };
  }

  const normalizedStart = new Date(start);
  normalizedStart.setHours(0, 0, 0, 0);
  const normalizedEnd = new Date(end);
  normalizedEnd.setHours(23, 59, 59, 999);
  return { start: normalizedStart, end: normalizedEnd };
}

function getBlockTone(blockType, hasUrlop = false) {
  if (hasUrlop || blockType === 'vacation') {
    return 'vacation';
  }

  if (blockType === 'break') {
    return 'break';
  }

  if (blockType === 'private') {
    return 'private';
  }

  return 'custom';
}

function getBlockMetaForDay(blocks, day) {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);

  const overlappingBlocks = blocks.filter((block) => {
    const interval = normalizeBlockInterval(block);
    return interval.start <= dayEnd && interval.end >= dayStart;
  });

  const hasUrlop = overlappingBlocks.some((block) => block.block_type === 'vacation' || String(block.reason || '').trim().toLowerCase() === 'urlop');

  return {
    hasBlock: overlappingBlocks.length > 0,
    hasUrlop,
    blockType: hasUrlop ? 'vacation' : (overlappingBlocks[0]?.block_type || null),
    blockLabel: hasUrlop ? 'Urlop' : (overlappingBlocks[0]?.reason || ''),
  };
}

function getFallbackOccupancyLevel(appointmentsCount, hasBlock) {
  if (hasBlock && appointmentsCount >= 2) {
    return 'full';
  }

  if (appointmentsCount >= 4) {
    return 'full';
  }

  if (appointmentsCount >= 2 || (hasBlock && appointmentsCount > 0)) {
    return 'medium';
  }

  return 'loose';
}

function formatVisitCount(count) {
  if (count === 1) {
    return '1 wizyta';
  }

  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return `${count} wizyty`;
  }

  return `${count} wizyt`;
}

function getDayReadinessSummary(appointments, dayBlocks) {
  const scheduledCount = appointments.filter((appointment) => appointment.status === 'scheduled').length;
  const resolvedCount = appointments.filter((appointment) => isAppointmentResolved(appointment)).length;
  const visitsValue = appointments
    .filter((appointment) => ['scheduled', 'completed'].includes(appointment.status))
    .reduce((sum, appointment) => sum + Number(appointment.price || 0), 0);
  const primaryBlock = dayBlocks[0] || null;

  return {
    scheduledCount,
    resolvedCount,
    visitsValue,
    primaryBlock,
    blockTone: getBlockTone(primaryBlock?.block_type, primaryBlock?.block_type === 'vacation'),
  };
}

export default function CalendarPage() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [viewMode, setViewMode] = useState('month');
  const [appointments, setAppointments] = useState([]);
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [monthSummary, setMonthSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddAppt, setShowAddAppt] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [deleteBlockTarget, setDeleteBlockTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchDay = useCallback(async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const { data } = await api.get(`/appointments?date=${dateStr}`);
      const sorted = [...data].sort((a, b) => {
        const aResolved = isAppointmentResolved(a);
        const bResolved = isAppointmentResolved(b);
        if (aResolved !== bResolved) {
          return aResolved ? 1 : -1;
        }
        return new Date(a.scheduled_at) - new Date(b.scheduled_at);
      });
      setAppointments(sorted);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRange = useCallback(async (startDateValue, endDateValue) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/appointments?startDate=${format(startDateValue, "yyyy-MM-dd'T'HH:mm:ss")}&endDate=${format(endDateValue, "yyyy-MM-dd'T'HH:mm:ss")}`);
      setWeekAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBlocks = useCallback(async () => {
    try {
      const { data } = await api.get('/availability/blocks');
      setBlocks(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchMonthSummary = useCallback(async (monthValue) => {
    try {
      const month = typeof monthValue === 'string' ? monthValue : format(monthValue, 'yyyy-MM');
      const { data } = await api.get(`/appointments/calendar-summary?month=${month}`);
      const mapped = {};
      data.forEach((item) => {
        mapped[item.date] = item;
      });
      setMonthSummary((previous) => ({ ...previous, ...mapped }));
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchUpcoming = useCallback(async (date) => {
    try {
      const { data } = await api.get('/appointments?status=scheduled');
      const selectedEnd = endOfDay(date).getTime();
      const next = data
        .filter((appt) => new Date(appt.scheduled_at).getTime() > selectedEnd)
        .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
        .slice(0, 5);
      setUpcomingAppointments(next);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const refreshCalendarData = useCallback(async () => {
    const tasks = [fetchUpcoming(selectedDay), fetchBlocks(), fetchMonthSummary(currentMonth)];

    if (viewMode === 'week') {
      const weekStart = startOfWeek(selectedDay, { weekStartsOn: 1 });
      const weekEnd = addDays(endOfWeek(selectedDay, { weekStartsOn: 1 }), 1);
      tasks.unshift(fetchRange(weekStart, weekEnd));
    } else {
      tasks.unshift(fetchDay(selectedDay));
    }

    await Promise.all(tasks);
  }, [currentMonth, fetchBlocks, fetchDay, fetchMonthSummary, fetchRange, fetchUpcoming, selectedDay, viewMode]);

  function handleDayClick(day) {
    setSelectedDay(day);
    setCurrentMonth(day);
  }

  function handleCalendarBack() {
    if (viewMode === 'week') {
      const nextDay = subDays(selectedDay, 7);
      setSelectedDay(nextDay);
      setCurrentMonth(nextDay);
      return;
    }

    if (viewMode === 'day') {
      const nextDay = subDays(selectedDay, 1);
      setSelectedDay(nextDay);
      setCurrentMonth(nextDay);
      return;
    }

    setCurrentMonth(subMonths(currentMonth, 1));
  }

  function handleCalendarForward() {
    if (viewMode === 'week') {
      const nextDay = addDays(selectedDay, 7);
      setSelectedDay(nextDay);
      setCurrentMonth(nextDay);
      return;
    }

    if (viewMode === 'day') {
      const nextDay = addDays(selectedDay, 1);
      setSelectedDay(nextDay);
      setCurrentMonth(nextDay);
      return;
    }

    setCurrentMonth(addMonths(currentMonth, 1));
  }

  function handleCallClick(phone) {
    openPhoneDialer(phone);
  }

  function getAppointmentPhone(appointment) {
    return appointment?.client_phone || appointment?.phone || appointment?.client?.phone || '';
  }

  function buildAppointmentSmsMessage(appointment) {
    const appointmentDate = new Date(appointment.scheduled_at);
    const clientName = appointment?.client_name || 'Cześć';

    if (isSameDay(appointmentDate, new Date())) {
      return `Cześć ${clientName}! Przypominam o wizycie dziś o ${format(appointmentDate, 'HH:mm')}. Do zobaczenia!`;
    }

    return `Cześć ${clientName}! Przypominam o wizycie ${format(appointmentDate, 'EEEE d MMMM', { locale: pl })} o ${format(appointmentDate, 'HH:mm')}. Do zobaczenia!`;
  }

  function handleSMSClick(appointment) {
    const phone = getAppointmentPhone(appointment);
    if (!phone) {
      setToast({ type: 'error', message: 'Brak numeru telefonu dla tej wizyty.' });
      return;
    }
    openSmsComposer(phone, buildAppointmentSmsMessage(appointment));
  }

  async function handleDeleteBlock(id) {
    if (!deleteBlockTarget) return;
    try {
      await api.delete(`/availability/blocks/${id}`);
      setToast({ type: 'success', message: 'Blokada została usunięta.' });
      await refreshCalendarData();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Nie udało się usunąć blokady.' });
    }
  }

  async function handleStatusChange(id, status) {
    try {
      await api.put(`/appointments/${id}`, { status });
      setToast({ type: 'success', message: 'Status wizyty został zaktualizowany.' });
      await refreshCalendarData();
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Nie udało się zmienić statusu wizyty.' });
    }
  }

  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  });

  // Dni tygodnia od poniedziałku
  const weekDays = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd'];
  const firstDayOffset = (startOfMonth(currentMonth).getDay() + 6) % 7;
  const visibleWeekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(startOfWeek(selectedDay, { weekStartsOn: 1 }), index)),
    [selectedDay]
  );
  const appointmentsByDay = weekAppointments.reduce((map, appointment) => {
    const dayKey = format(new Date(appointment.scheduled_at), 'yyyy-MM-dd');
    if (!map[dayKey]) {
      map[dayKey] = [];
    }
    map[dayKey].push(appointment);
    map[dayKey].sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    return map;
  }, {});
  const visibleWeekMonths = useMemo(
    () => Array.from(new Set(visibleWeekDays.map((day) => format(day, 'yyyy-MM')))),
    [visibleWeekDays]
  );
  const weekTimelineDays = useMemo(() => {
    return visibleWeekDays.map((day) => {
      const dayKey = format(day, 'yyyy-MM-dd');
      const dayAppointments = appointmentsByDay[dayKey] || [];
      const fallbackMeta = getBlockMetaForDay(blocks, day);
      const dayMeta = monthSummary[dayKey]
        ? {
          ...monthSummary[dayKey],
          hasBlock: monthSummary[dayKey].hasBlock ?? fallbackMeta.hasBlock,
          hasUrlop: monthSummary[dayKey].hasUrlop ?? fallbackMeta.hasUrlop,
          blockType: monthSummary[dayKey].blockType || fallbackMeta.blockType,
          blockLabel: monthSummary[dayKey].blockLabel || fallbackMeta.blockLabel,
          appointmentsCount: monthSummary[dayKey].appointmentsCount ?? dayAppointments.length,
          occupancyLevel: monthSummary[dayKey].occupancyLevel || getFallbackOccupancyLevel(dayAppointments.length, fallbackMeta.hasBlock),
        }
        : {
          ...fallbackMeta,
          appointmentsCount: dayAppointments.length,
          occupancyLevel: getFallbackOccupancyLevel(dayAppointments.length, fallbackMeta.hasBlock),
        };

      const dayBlocks = blocks
        .map((block) => {
          const interval = normalizeBlockInterval(block);
          const clipped = clipIntervalToDay(interval.start, interval.end, day);
          if (!clipped) {
            return null;
          }

          return {
            ...block,
            start: clipped.start,
            end: clipped.end,
            tone: getBlockTone(block.block_type, block.block_type === 'vacation' || String(block.reason || '').trim().toLowerCase() === 'urlop'),
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.start - right.start);

      return {
        key: dayKey,
        day,
        meta: dayMeta,
        appointments: dayAppointments,
        blocks: dayBlocks,
      };
    });
  }, [appointmentsByDay, blocks, monthSummary, visibleWeekDays]);
  const weekTimelineBounds = useMemo(() => getTimelineBounds(weekTimelineDays), [weekTimelineDays]);
  const selectedWeekDay = useMemo(
    () => weekTimelineDays.find(({ day }) => isSameDay(day, selectedDay)) || weekTimelineDays[0] || null,
    [selectedDay, weekTimelineDays]
  );
  const timelineHours = useMemo(
    () => Array.from({ length: weekTimelineBounds.endHour - weekTimelineBounds.startHour + 1 }, (_, index) => weekTimelineBounds.startHour + index),
    [weekTimelineBounds.endHour, weekTimelineBounds.startHour]
  );
  const timelineHeight = weekTimelineBounds.totalMinutes / 60 * TIMELINE_HOUR_HEIGHT;
  const selectedDayBlocks = useMemo(() => {
    return blocks.filter((block) => {
      const interval = normalizeBlockInterval(block);
      const dayStart = new Date(selectedDay);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(selectedDay);
      dayEnd.setHours(23, 59, 59, 999);
      return interval.start <= dayEnd && interval.end >= dayStart;
    });
  }, [blocks, selectedDay]);
  const selectedDaySummary = useMemo(
    () => getDayReadinessSummary(appointments, selectedDayBlocks),
    [appointments, selectedDayBlocks]
  );
  const selectedDayKpis = [
    { key: 'day-total', label: 'Wszystkie wizyty', value: appointments.length, featured: true },
    { key: 'day-scheduled', label: 'Do obsłużenia', value: selectedDaySummary.scheduledCount, tone: 'neutral' },
    { key: 'day-resolved', label: 'Rozliczone', value: selectedDaySummary.resolvedCount, tone: 'soft' },
    { key: 'day-value', label: 'Wartość dnia', value: Math.round(selectedDaySummary.visitsValue || 0), unit: 'zł', tone: 'accent' },
  ];

  useEffect(() => {
    if (viewMode === 'week') {
      const weekStart = startOfWeek(selectedDay, { weekStartsOn: 1 });
      const weekEnd = addDays(endOfWeek(selectedDay, { weekStartsOn: 1 }), 1);
      fetchRange(weekStart, weekEnd);
    } else {
      fetchDay(selectedDay);
    }
    fetchUpcoming(selectedDay);
    fetchBlocks();
  }, [selectedDay, viewMode, fetchDay, fetchRange, fetchBlocks, fetchUpcoming]);

  useEffect(() => {
    fetchMonthSummary(currentMonth);
  }, [currentMonth, fetchMonthSummary]);

  useEffect(() => {
    if (viewMode !== 'week') {
      return;
    }

    visibleWeekMonths.forEach((monthKey) => {
      void fetchMonthSummary(monthKey);
    });
  }, [fetchMonthSummary, viewMode, visibleWeekMonths]);

  const calendarNavLabel = viewMode === 'week'
    ? `${format(visibleWeekDays[0], 'd MMM', { locale: pl })} – ${format(visibleWeekDays[6], 'd MMM yyyy', { locale: pl })}`
    : viewMode === 'day'
      ? format(selectedDay, 'EEEE, d MMMM yyyy', { locale: pl })
      : format(currentMonth, 'LLLL yyyy', { locale: pl });

  return (
    <div className="panel-page calendar-page">
      <PanelToast toast={toast} onClose={() => setToast(null)} />

      <div className="panel-header">
        <div className="panel-header-copy">
          <span className="panel-eyebrow">Planowanie</span>
          <h1 className="panel-title">Kalendarz wizyt</h1>
          <p className="panel-subtitle">Obłożenie miesiąca, blokady i szybkie zarządzanie terminami.</p>
        </div>
      </div>

      <div className="calendar-view-switch">
        <button className={`tab-btn ${viewMode === 'month' ? 'tab-btn--active' : ''}`} onClick={() => setViewMode('month')}>Miesiąc</button>
        <button className={`tab-btn ${viewMode === 'week' ? 'tab-btn--active' : ''}`} onClick={() => setViewMode('week')}>Tydzień</button>
        <button className={`tab-btn ${viewMode === 'day' ? 'tab-btn--active' : ''}`} onClick={() => setViewMode('day')}>Dzień</button>
      </div>

      {/* Nawigacja miesiąca */}
      <div className="cal-nav">
        <button className="cal-nav-btn" onClick={handleCalendarBack}>
          ‹
        </button>
        <span className="cal-month-label">
          {calendarNavLabel}
        </span>
        <button className="cal-nav-btn" onClick={handleCalendarForward}>
          ›
        </button>
      </div>

      {/* Siatka kalendarza */}
      {viewMode === 'month' && (
      <div className="cal-grid">
        {weekDays.map((d) => (
          <div key={d} className="cal-weekday">{d}</div>
        ))}
        {Array.from({ length: firstDayOffset }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {days.map((day) => {
          const isSelected = isSameDay(day, selectedDay);
          const isToday = isSameDay(day, new Date());
          const dayKey = format(day, 'yyyy-MM-dd');
          const dayMeta = monthSummary[dayKey] || { appointmentsCount: 0, hasUrlop: false, hasBlock: false, blockLabel: '' };
          const blockTone = getBlockTone(dayMeta.blockType, dayMeta.hasUrlop);
          const dayLoadTone = dayMeta.hasBlock
            ? 'warning'
            : (isToday || dayMeta.appointmentsCount > 1 ? 'accent' : 'muted');

          return (
            <button
              key={day.toISOString()}
              className={`cal-day ${isSelected ? 'cal-day--selected' : ''} ${isToday ? 'cal-day--today' : ''} ${dayMeta.hasUrlop ? 'cal-day--urlop' : ''} ${!dayMeta.hasUrlop && dayMeta.hasBlock ? 'cal-day--blocked' : ''} ${dayMeta.hasBlock ? `cal-day--block-${blockTone}` : ''}`}
              onClick={() => handleDayClick(day)}
              title={dayMeta.hasBlock ? dayMeta.blockLabel || 'Blokada' : undefined}
            >
              <span className="cal-day-number">{format(day, 'd')}</span>
              {dayMeta.appointmentsCount > 0 && (
                <span className={`cal-day-load cal-day-load--${dayLoadTone}`}>
                  {dayMeta.appointmentsCount}
                </span>
              )}
            </button>
          );
        })}
      </div>
      )}

      {viewMode === 'week' && (
        <>
          {selectedWeekDay && (
            <div className="calendar-week-mobile-summary">
              <div className="calendar-week-mobile-copy">
                <strong>{format(selectedWeekDay.day, 'EEEE, d MMMM', { locale: pl })}</strong>
                <span>
                  {formatVisitCount(selectedWeekDay.meta?.appointmentsCount || 0)}
                  {selectedWeekDay.meta?.hasBlock ? ` · ${selectedWeekDay.meta.blockLabel || 'Blokada'}` : ''}
                </span>
              </div>
              <div className="calendar-week-mobile-actions">
                <button type="button" className="btn-secondary" onClick={() => setViewMode('day')}>
                  Pokaż dzień
                </button>
              </div>
            </div>
          )}
          <div className="calendar-week-timeline">
            <div className="calendar-week-timeline-scroll">
            <div className="calendar-week-timeline-header">
              <div className="calendar-week-time-corner">Godz.</div>
              <div className="calendar-week-day-headers">
                {weekTimelineDays.map(({ key, day, meta }) => {
                  const blockTone = getBlockTone(meta.blockType, meta.hasUrlop);

                  return (
                    <button
                      key={`timeline-head-${key}`}
                      type="button"
                      className={`calendar-week-day-header ${isSameDay(day, new Date()) ? 'calendar-week-day-header--today' : ''} ${isSameDay(day, selectedDay) ? 'calendar-week-day-header--selected' : ''}`}
                      onClick={() => {
                        handleDayClick(day);
                        setViewMode('day');
                      }}
                    >
                      <span className="calendar-week-day-header-top">{format(day, 'EEE', { locale: pl })}</span>
                      <strong>{format(day, 'd MMM', { locale: pl })}</strong>
                      <span className="calendar-week-day-header-bottom">{formatVisitCount(meta.appointmentsCount || 0)}</span>
                      {meta.hasBlock && (
                        <span className={`calendar-week-day-pill ${meta.hasUrlop ? 'calendar-week-day-pill--urlop' : `calendar-week-day-pill--${blockTone}`}`}>
                          {meta.blockLabel || 'Blokada'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="calendar-week-timeline-body">
              <div className="calendar-week-time-scale" style={{ height: `${timelineHeight}px` }}>
                {timelineHours.map((hour, index) => (
                  <div key={`timeline-hour-${hour}`} className="calendar-week-time-row" style={{ top: `${index * TIMELINE_HOUR_HEIGHT}px` }}>
                    <span>{`${String(hour).padStart(2, '0')}:00`}</span>
                  </div>
                ))}
              </div>

              <div className="calendar-week-day-columns">
                {weekTimelineDays.map(({ key, day, meta, appointments: dayAppointments, blocks: dayBlocks }) => {
                  const blockTone = getBlockTone(meta.blockType, meta.hasUrlop);

                  return (
                    <div
                      key={`timeline-column-${key}`}
                      className={`calendar-week-day-column ${isSameDay(day, new Date()) ? 'calendar-week-day-column--today' : ''} ${isSameDay(day, selectedDay) ? 'calendar-week-day-column--selected' : ''} ${meta.hasBlock ? `calendar-week-day-column--${blockTone}` : ''}`}
                      style={{ height: `${timelineHeight}px` }}
                    >
                      {timelineHours.slice(0, -1).map((hour, index) => (
                        <div
                          key={`timeline-slot-${key}-${hour}`}
                          className="calendar-week-slot"
                          style={{ top: `${index * TIMELINE_HOUR_HEIGHT}px`, height: `${TIMELINE_HOUR_HEIGHT}px` }}
                        />
                      ))}

                      <button
                        type="button"
                        className="calendar-week-day-overlay"
                        aria-label={`Otwórz ${format(day, 'EEEE d MMMM', { locale: pl })}`}
                        onClick={() => {
                          handleDayClick(day);
                          setViewMode('day');
                        }}
                      />

                      {dayBlocks.map((block) => {
                        const blockStartMinutes = getMinutesOfDay(block.start) - weekTimelineBounds.startHour * 60;
                        const blockEndMinutes = getMinutesOfDay(block.end) - weekTimelineBounds.startHour * 60;
                        const top = Math.max(0, blockStartMinutes / 60 * TIMELINE_HOUR_HEIGHT);
                        const height = Math.max(28, (blockEndMinutes - blockStartMinutes) / 60 * TIMELINE_HOUR_HEIGHT);

                        return (
                          <div
                            key={`timeline-block-${block.id}-${key}`}
                            className={`calendar-week-timeline-item calendar-week-timeline-item--block calendar-week-timeline-item--${block.tone}`}
                            style={{ top: `${top}px`, height: `${height}px` }}
                            title={`${block.reason || 'Blokada'} · ${formatBlockRange(block)}`}
                          >
                            <strong>{block.reason || 'Blokada'}</strong>
                            <span>{`${format(block.start, 'HH:mm')} - ${format(block.end, 'HH:mm')}`}</span>
                          </div>
                        );
                      })}

                      {dayAppointments.map((appointment) => {
                        const start = new Date(appointment.scheduled_at);
                        const durationMinutes = Math.max(Number(appointment.duration_minutes || 0), 30);
                        const end = new Date(start.getTime() + durationMinutes * 60000);
                        const startMinutes = getMinutesOfDay(start) - weekTimelineBounds.startHour * 60;
                        const endMinutes = getMinutesOfDay(end) - weekTimelineBounds.startHour * 60;
                        const top = Math.max(0, startMinutes / 60 * TIMELINE_HOUR_HEIGHT);
                        const height = Math.max(40, (endMinutes - startMinutes) / 60 * TIMELINE_HOUR_HEIGHT);

                        return (
                          <div
                            key={`timeline-appointment-${appointment.id}`}
                            className="calendar-week-timeline-item calendar-week-timeline-item--appointment"
                            style={{ top: `${top}px`, height: `${height}px` }}
                            title={`${appointment.client_name || 'Klientka'} · ${appointment.service_name || 'Wizyta'}`}
                          >
                            <span className="calendar-week-timeline-time">{format(start, 'HH:mm')}</span>
                            <strong>{appointment.client_name || 'Klientka'}</strong>
                            <span>{appointment.service_name || 'Wizyta'}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          </div>
        </>
      )}

      {/* Wybrany dzień */}
      {viewMode !== 'week' && (
      <div className="day-section">
        <div className="day-section-header">
          <h2 className="day-section-title">
            {format(selectedDay, "EEEE, d MMMM", { locale: pl })}
          </h2>
          <div className="day-section-actions">
            <button className="quick-btn small" onClick={() => setShowAddAppt(true)}>
              Dodaj wizytę
            </button>
            <button className="quick-btn quick-btn-outline small" onClick={() => setShowAddBlock(true)}>
              Przerwa / blokada
            </button>
          </div>
        </div>

        <div className="calendar-day-layout">
          <div className="calendar-day-summary">
            <KpiStrip
              items={selectedDayKpis}
              variant="calendar"
              className="calendar-kpi-strip"
              ariaLabel="Podsumowanie wybranego dnia"
            />
          </div>

          <div className="calendar-day-primary">
            {selectedDayBlocks.length > 0 && (
              <div className="calendar-day-blocks">
                {selectedDayBlocks.map((block) => {
                  const blockTone = getBlockTone(block.block_type, block.block_type === 'vacation');

                  return (
                    <div key={`selected-day-block-${block.id}`} className={`calendar-day-block calendar-day-block--${blockTone}`}>
                      <div className="calendar-day-block-copy">
                        <strong>{block.reason || 'Blokada'}</strong>
                        <span>{formatBlockRange(block)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {loading && <p className="loading-text">Ładowanie...</p>}

            {!loading && appointments.length === 0 && (
              <div className={`panel-empty-state ${selectedDayBlocks.length > 0 ? 'panel-empty-state--blocked' : ''}`}>
                <strong>
                  {selectedDayBlocks.length > 0
                    ? `${selectedDaySummary.primaryBlock?.reason || 'Ten dzień ma aktywną blokadę'}`
                    : 'Brak wizyt tego dnia.'}
                </strong>
                <p>
                  {selectedDayBlocks.length > 0
                    ? 'Dzień jest częściowo lub całkowicie zajęty blokadą. Możesz ją usunąć niżej albo dodać wizytę w innym terminie.'
                    : 'To dobry moment, żeby dodać wizytę ręcznie albo zostawić dzień jako wolny.'}
                </p>
                <div className="panel-empty-state-actions">
                  <button type="button" className="btn-primary" onClick={() => setShowAddAppt(true)}>
                    Dodaj wizytę
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setShowAddBlock(true)}>
                    Dodaj blokadę
                  </button>
                  <button type="button" className="btn-secondary" onClick={() => setViewMode('week')}>
                    Podejrzyj tydzień
                  </button>
                </div>
              </div>
            )}

            {appointments.map((appt) => {
              return (
                <AppointmentCard
                  key={appt.id}
                  appointment={appt}
                  timeLabel={format(new Date(appt.scheduled_at), 'HH:mm')}
                  onCall={() => handleCallClick(appt.client_phone)}
                  onSms={() => handleSMSClick(appt)}
                  onReschedule={() => setRescheduleTarget(appt)}
                  onSetStatus={(status) => handleStatusChange(appt.id, status)}
                />
            );})}
          </div>
        </div>
      </div>
      )}

      {upcomingAppointments.length > 0 && (
        <div className="section">
          <h2 className="section-title">Nadchodzące wizyty</h2>
          {upcomingAppointments.map((appt) => (
            <AppointmentCard
              key={`upcoming-${appt.id}`}
              appointment={appt}
              timeLabel={format(new Date(appt.scheduled_at), 'dd.MM HH:mm')}
              onCall={() => handleCallClick(appt.client_phone)}
              onSms={() => handleSMSClick(appt)}
              onReschedule={() => setRescheduleTarget(appt)}
              onSetStatus={(status) => handleStatusChange(appt.id, status)}
            />
          ))}
        </div>
      )}

      {/* Aktywne blokady */}
      {blocks.length > 0 && (
        <div className="section">
          <h2 className="section-title">Aktywne blokady</h2>
          {blocks.map((block) => {
            return (
            <div key={block.id} className="block-card">
              <div className="block-info">
                <span className="block-reason">{block.reason}</span>
                <span className="block-time">
                  {formatBlockRange(block)}
                </span>
              </div>
              <button
                className="icon-btn icon-btn-danger"
                onClick={() => setDeleteBlockTarget(block)}
              >
                ✕
              </button>
            </div>
          );})}
        </div>
      )}

      {showAddAppt && (
        <AddAppointmentModal
          defaultDate={selectedDay}
          onClose={() => setShowAddAppt(false)}
          onSaved={() => {
            setShowAddAppt(false);
            setToast({ type: 'success', message: 'Wizyta została dodana.' });
            void refreshCalendarData();
          }}
        />
      )}
      {showAddBlock && (
        <AddBlockModal
          defaultDate={selectedDay}
          onClose={() => setShowAddBlock(false)}
          onSaved={() => {
            setShowAddBlock(false);
            setToast({ type: 'success', message: 'Blokada została dodana.' });
            void refreshCalendarData();
          }}
        />
      )}
      {rescheduleTarget && (
        <RescheduleAppointmentModal
          appointment={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onSaved={() => {
            setRescheduleTarget(null);
            setToast({ type: 'success', message: 'Termin wizyty został zmieniony.' });
            void refreshCalendarData();
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(deleteBlockTarget)}
        title="Usunąć blokadę?"
        description={deleteBlockTarget
          ? `Usunę blokadę „${deleteBlockTarget.reason || 'Blokada'}” z grafiku.`
          : ''}
        confirmLabel="Usuń blokadę"
        cancelLabel="Zostaw"
        confirmTone="danger"
        onCancel={() => setDeleteBlockTarget(null)}
        onConfirm={() => {
          const blockId = deleteBlockTarget?.id;
          setDeleteBlockTarget(null);
          if (blockId) {
            void handleDeleteBlock(blockId);
          }
        }}
      />

      <BottomNav active="calendar" />
    </div>
  );
}
